import React from "react";

/**
 * WavyFrame — a two-line wavy rectangle border drawn as inline SVG.
 * Sized via CSS (fills its parent). Background-fill transparent.
 */
export default function WavyFrame({
  color = "#5F6B43",
  strokeWidth = 0.6,
  waves = 7,
  amp = 1.6,
  gap = 1.2,
  inset = 2.5,
  className = "",
  testid = "wavy-frame",
}) {
  // viewBox 0..100 square; preserveAspectRatio none → stretches with parent
  const path = (offset) => buildWavyRect(100, 100, inset + offset, waves, amp);
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className={`pointer-events-none ${className}`}
      style={{ width: "100%", height: "100%" }}
      aria-hidden="true"
      data-testid={testid}
    >
      <path d={path(0)} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" strokeLinecap="round" />
      <path d={path(gap)} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function buildWavyRect(w, h, inset, waves, amp) {
  const x0 = inset, y0 = inset, x1 = w - inset, y1 = h - inset;
  let d = "";
  // Top: left -> right, waves curving outward (up) then inward (down)
  d += `M ${x0} ${y0} `;
  d += sineSide(x0, x1, y0, true, "h", waves, amp);
  // Right: top -> bottom
  d += sineSide(y0, y1, x1, true, "v", waves, amp);
  // Bottom: right -> left
  d += sineSide(x1, x0, y1, false, "h", waves, amp);
  // Left: bottom -> top
  d += sineSide(y1, y0, x0, false, "v", waves, amp);
  d += "Z";
  return d;
}

/**
 * Build a chain of cubic beziers approximating a sine wave along one side of the rect.
 * orient="h" → x varies from a..b at constant y=fixed
 * orient="v" → y varies from a..b at constant x=fixed
 */
function sineSide(a, b, fixed, forward, orient, waves, amp) {
  const total = b - a;
  const step = total / waves;
  let d = "";
  for (let i = 0; i < waves; i++) {
    const s = a + i * step;
    const e = a + (i + 1) * step;
    const mid = (s + e) / 2;
    // Sign of amplitude alternates; first bump is outward (negative on top => smaller y)
    const sign = i % 2 === 0 ? -1 : 1;
    const off = sign * amp;
    let cp1, cp2, end;
    if (orient === "h") {
      cp1 = `${s + step / 3} ${fixed + off * 2}`;
      cp2 = `${e - step / 3} ${fixed + off * 2}`;
      end = `${e} ${fixed}`;
    } else {
      cp1 = `${fixed + off * 2} ${s + step / 3}`;
      cp2 = `${fixed + off * 2} ${e - step / 3}`;
      end = `${fixed} ${e}`;
    }
    d += `C ${cp1}, ${cp2}, ${end} `;
  }
  return d;
}
